/**
 * The reason a Rechnungskorrektur states, in German.
 *
 * Named and exported because the document itself is always German - every label the PDF prints is
 * a German literal - while the editor that picks the reason runs in the user's own language. The
 * PDF resolves the stored preset through this map rather than printing whatever the picker's
 * language happened to render, so an English UI cannot put English words on a German invoice.
 */
export const CORRECTION_REASONS_DE = {
  quantityOverCharged: 'Menge zu hoch berechnet',
  positionDisputed: 'Position reklamiert',
  goodwill: 'Kulanz',
  priceReduction: 'Preisnachlass',
} as const

const invoiceDetails = {
  title: {
    invoice: 'Rechnung',
    quote: 'Angebot',
    supplementaryOffer: 'Nachtragsangebot',
    quoteConfirmation: 'Angebotsbestätigung',
    orderConfirmation: 'Auftragsbestätigung',
    creditNote: 'Gutschrift',
    invoiceCorrection: 'Rechnungskorrektur',
    stornoInvoice: 'Stornorechnung',
    deliveryNote: 'Lieferschein',
    partialInvoice: 'Teilrechnung',
    installmentInvoice: 'Abschlagsrechnung',
    finalInvoice: 'Schlussrechnung',
    recurringInvoice: 'Wiederkehrende Rechnung',
  },
  overview: {
    title: 'Übersicht',
    job: 'Auftrag',
    client: 'Kunde',
    totalAmount: 'Gesamtbetrag',
    paymentTerm: 'Zahlungsbedingungen',
    clientTerms: 'Kundenbedingungen',
    linkedDocuments: 'Folgedokumente',
    createdFrom: 'Erstellt aus',
    issueDate: 'Ausstellungsdatum',
    fulfillmentDate: 'Leistungsdatum',
    dueDate: 'Fälligkeitsdatum',
    deliveryDate: 'Lieferdatum',
    number: 'Nummer',
    expiresFrom: 'Ablaufdatum',
    recurring: 'Rechnungen',
    interval: 'Intervall',
    automatedReminders: 'Automatische Erinnerungen',
    activePaymentReminders: 'Aktive Zahlungserinnerungen werden ausgelöst am:',
    activeOverdueReminders: 'Aktive Mahnungen werden ausgelöst am:',
    automatedRemindersUpdated: 'Automatische Erinnerungen aktualisiert',
    automatedRemindersUpdateFailed: 'Automatische Erinnerungen konnten nicht aktualisiert werden',
    buyerReference: 'Käuferreferenz (Leitweg-ID)',
    buyerReferencePlaceholder: 'z. B. die Leitweg-ID deines Kunden',
    buyerReferenceUpdated: 'Käuferreferenz aktualisiert',
    buyerReferenceUpdateFailed: 'Käuferreferenz konnte nicht aktualisiert werden',
    tags: 'Tags',
    supplementaryTo: 'Nachtrag zu',
  },
  payments: {
    title: 'Zahlungen',
    amount: 'Betrag',
    date: 'Datum',
    totalPaid: 'Gesamt bezahlt',
    remainingAmount: 'Restbetrag',
    noPayments: 'Noch keine Zahlungen erfasst',
    actions: 'Zahlungsaktionen',
    edit: 'Bearbeiten',
    delete: 'Löschen',
    addPayment: 'Teilzahlung hinzufügen',
  },
  capturePaymentDialog: {
    title: 'Zahlung erfassen',
    description: 'Erfasse eine erhaltene Zahlung für diese Rechnung.',
    invoiceAmount: 'Rechnungsbetrag',
    remainingAmount: 'Restbetrag',
    paymentAmount: 'Zahlungsbetrag',
    paymentDate: 'Zahlungsdatum',
    selectDate: 'Datum auswählen',
    cancel: 'Abbrechen',
    capturePayment: 'Zahlung erfassen',
    editTitle: 'Zahlung bearbeiten',
    editDescription: 'Korrigiere Betrag oder Datum dieser Zahlung.',
    savePayment: 'Zahlung speichern',
    errors: {
      amountMustBePositive: 'Zahlungsbetrag muss größer als 0 sein',
      amountTooHigh: 'Zahlungsbetrag kann {{max}} nicht überschreiten',
    },
  },
  laborHours: {
    sectionLabel: 'Arbeitsstunden',
    costLabel: 'Kosten der Arbeitsstunden',
  },
  recurringInvoiceDialog: {
    title: 'Wiederkehrende Rechnung erstellen',
    description:
      'Setze einen Zeitplan für das automatische Generieren von Rechnungen basierend auf Ihrer angegebenen Frequenz und Dauer.',
    recurrenceSection: 'Intervall',
    emailSection: 'E-Mail',
    startDate: 'Startdatum',
    frequency: 'Frequenz',
    frequencyPlaceholder: 'Frequenz auswählen',
    frequencyDaily: 'Täglich',
    frequencyWeekly: 'Wöchentlich',
    frequencyMonthly: 'Monatlich',
    frequencyQuarterly: 'Quartalsweise',
    frequencyYearly: 'Jährlich',
    interval: 'Intervall',
    sendEvery: 'Verschickt alle',
    frequencyDays: 'Tage',
    frequencyWeeks: 'Wochen',
    frequencyMonths: 'Monate',
    frequencyQuarters: 'Quartale',
    frequencyYears: 'Jahre',
    frequencyDay: 'Tag',
    frequencyWeek: 'Woche',
    frequencyMonth: 'Monat',
    frequencyQuarter: 'Quartal',
    frequencyYear: 'Jahr',
    endCondition: 'Endbedingung',
    endDateCondition: 'Enddatum',
    maxOccurrencesCondition: 'Maximale Anzahl',
    end: 'Ende',
    after: 'Nach',
    date: 'Datum',
    occurrences: 'Wiederholungen',
    endDate: 'Enddatum',
    maxOccurrences: 'Maximale Anzahl',
    maxOccurrencesPlaceholder: 'z.B. 12',
    cancel: 'Abbrechen',
    createRecurringInvoice: 'Wiederkehrende Rechnung erstellen',
    creating: 'Erstellen...',
    validationEndConditionRequired:
      'Bitte geben Sie entweder ein Enddatum oder eine maximale Anzahl an',
  },
  action: {
    downloadPdf: 'Herunterladen',
    downloadXml: 'e-Rechnung (XML) herunterladen',
    printPdf: 'Drucken',
    decline: 'Ablehnen',
    createInvoice: 'Rechnung erstellen',
    createQuote: 'Angebot erstellen',
    send: 'E-Mail',
    markAsPaid: 'Bezahlt',
    createCorrection: 'Rechnungskorrektur erstellen',
    createStornoInvoice: 'Stornorechnung erstellen',
    void: 'Stornieren',
    issue: 'Abschließen',
    delete: 'Löschen',
    accept: 'Akzeptieren',
    createDeliveryNote: 'Lieferschein erstellen',
    createQuoteConfirmation: 'Angebotsbestätigung erstellen',
    createOrderConfirmation: 'Auftragsbestätigung erstellen',
    markAsDelivered: 'Geliefert',
    pauseRecurrence: 'Pausieren',
    resumeRecurrence: 'Fortsetzen',
    cancelRecurrence: 'Abbrechen',
    createPartialInvoice: 'Teil-/Abschlagsrechnung',
    createInstallmentInvoice: 'Abschlagsrechnung erstellen',
    createFinalInvoice: 'Schlussrechnung erstellen',
    createRecurringInvoice: 'Wiederkehrende Rechnung erstellen',
    createSupplementaryOffer: 'Nachtragsangebot erstellen',
  },
  supplementaryOffers: {
    sectionLabel: 'Nachtragsangebote',
    originalAmount: 'Ursprünglicher Betrag',
    acceptedAmount: 'Akzeptierte Nachtragsangebote',
    projectedTotal: 'Voraussichtliche Auftragssumme',
  },
  sections: {
    document: 'Dokument',
    customerAndJob: 'Kunde und Auftrag',
    payment: 'Zahlung',
    linkedDocuments: 'Verknüpfte Dokumente',
  },
  summary: {
    open: 'Offen',
    dueIn_one: 'Fällig am {{date}} · in {{count}} Tag',
    dueIn_other: 'Fällig am {{date}} · in {{count}} Tagen',
    dueToday: 'Fällig am {{date}} · heute fällig',
    overdue_one: 'Fällig am {{date}} · seit {{count}} Tag überfällig',
    overdue_other: 'Fällig am {{date}} · seit {{count}} Tagen überfällig',
    validUntil: 'Gültig bis {{date}}',
  },
  partialInvoiceChain: {
    subtitle_one: 'Abrechnung zu {{documentId}} · {{count}} Rechnung',
    subtitle_other: 'Abrechnung zu {{documentId}} · {{count}} Rechnungen',
    subtitleWithSupplements_one:
      'Abrechnung zu {{documentId}} · {{count}} Rechnung · inkl. Nachtragsangebote',
    subtitleWithSupplements_other:
      'Abrechnung zu {{documentId}} · {{count}} Rechnungen · inkl. Nachtragsangebote',
    ofTotalBilled: 'von {{total}} abgerechnet',
    bar: 'Bezahlt {{paid}}, offen {{open}}, nicht abgerechnet {{notBilled}}',
    paid: 'Bezahlt',
    open: 'Offen',
    notBilled: 'Nicht abgerechnet',
    quote: 'Angebot',
    partialInvoice: 'Teilrechnung {{n}}',
    installmentInvoice: 'Abschlagsrechnung {{n}}',
    finalInvoice: 'Schlussrechnung',
    current: 'Aktuell',
    noNumberYet: 'Noch ohne Nummer',
    restOfQuote: 'Rest des Angebots',
    dueOn: 'fällig {{date}}',
    percent: '{{value}} %',
    amountPaid: '{{amount}} bezahlt',
  },
  corrections: {
    title: 'Rechnungskorrekturen',
    creditedTotal: 'Bereits korrigiert',
    openAmount: 'Offener Betrag',
  },
  correction: {
    title: 'Rechnungskorrektur erstellen',
    description: 'Positionen der Rechnung {{documentId}} gutschreiben.',
    column: {
      position: 'Position',
      remaining: 'Noch korrigierbar',
      mode: 'Art',
      value: 'Wert',
    },
    mode: {
      quantity: 'Menge',
      amount: 'Betrag',
    },
    creditedTotal: 'Korrekturbetrag (netto)',
    grossTotal: 'Korrekturbetrag (brutto)',
    openAmount: 'Offener Betrag der Rechnung',
    exceedsLine: 'Nur noch {{amount}} korrigierbar.',
    reason: {
      label: 'Grund der Korrektur',
      ...CORRECTION_REASONS_DE,
      placeholder: 'Ergänzende Erläuterung (optional)',
    },
    settlement: {
      label: 'Ausgleich des übersteigenden Betrags',
      refund: 'Rückzahlung',
      offset_next_invoice: 'Mit nächster Rechnung verrechnen',
      refunded_in_cash: 'Bar zurückgezahlt',
      written_off: 'Ausgebucht',
    },
    issueSuccess: 'Rechnungskorrektur ausgestellt',
    submit: 'Korrektur als Entwurf anlegen',
    success: 'Rechnungskorrektur als Entwurf angelegt',
    creditedSoFar: 'Bereits korrigiert',
  },
  errors: {
    positionSelectionEmpty: 'Wählen Sie mindestens eine Position aus.',
    positionSelectionUnknown:
      'Eine gewählte Position gehört nicht mehr zum Ausgangsdokument. Laden Sie neu und wählen Sie erneut.',
    xrechnungSellerIdentifierMissing:
      'Eine E-Rechnung benötigt eine Umsatzsteuer-ID oder eine Steuernummer Ihres Unternehmens. Hinterlegen Sie eine in den Firmeneinstellungen.',
    xrechnungNegativeUnitPrice:
      'Eine E-Rechnung darf keinen negativen Einzelpreis enthalten. Erfassen Sie den Abzug stattdessen als negative Menge zu einem positiven Preis.',
    partialInvoiceNegativeLines:
      'Dieses Angebot enthält Positionen mit negativem Preis oder negativer Menge. Teil- und Abschlagsrechnungen sind dafür noch nicht verfügbar. Erstellen Sie stattdessen eine vollständige Rechnung.',
    creditNoteNegativeLines:
      'Diese Rechnung enthält Positionen mit negativem Preis oder negativer Menge und kann noch nicht positionsweise korrigiert werden. Stornieren Sie sie stattdessen.',
    negativeAmountsDisabled:
      'Negative Preise und Mengen sind für dieses Unternehmen nicht aktiviert. Aktiviere die Funktion in den Einstellungen unter „Beta-Funktionen".',
    creditNoteFeatureDisabled:
      'Rechnungskorrekturen sind für dieses Unternehmen nicht aktiviert. Aktiviere die Funktion in den Einstellungen unter „Beta-Funktionen".',
    creditNoteNotAllowedForInvoiceType:
      'Für Teil-, Abschlags- und Schlussrechnungen kann keine Rechnungskorrektur erstellt werden. Diese Dokumente verrechnen bereits andere Rechnungen miteinander.',
    creditNoteInvoiceAlreadyCorrected:
      'Diese Rechnung wurde bereits korrigiert und kann nicht mehr storniert werden. Korrigieren Sie stattdessen den verbleibenden Betrag.',
    creditNoteDraftAlreadyOpen:
      'Für diese Rechnung ist bereits ein Entwurf einer Rechnungskorrektur offen. Stellen Sie ihn aus oder löschen Sie ihn.',
    creditNoteNoLinesSelected: 'Wählen Sie mindestens eine Position aus.',
    creditNoteLineNotCreditable:
      'Mindestens eine ausgewählte Position gehört nicht zu dieser Rechnung. Bitte laden Sie die Seite neu.',
    creditNoteLineSelectedTwice: 'Jede Position kann nur einmal ausgewählt werden.',
    creditNoteAmountNotPositive: 'Der zu korrigierende Betrag muss größer als 0 sein.',
    creditNoteModeMismatch:
      'Die Position „{{title}}" wurde bereits auf andere Weise korrigiert. Verwenden Sie dieselbe Art der Korrektur wie zuvor.',
    creditNoteExceedsLine:
      'Für die Position „{{title}}" ist weniger offen, als korrigiert werden soll. Bitte laden Sie die Seite neu.',
    creditNoteExceedsInvoice:
      'Die Korrektur übersteigt den Betrag dieser Rechnung. Bitte laden Sie die Seite neu.',
    creditNoteNotCorrectable: 'Dieses Dokument ist keine Rechnungskorrektur im Entwurf.',
    partialInvoiceAllItemsSelectedError:
      'Es können nicht alle verbleibenden Positionen ausgewählt werden. Verwenden Sie stattdessen eine Schlussrechnung.',
    draftInstallmentInvoiceExistsError:
      'Es kann keine neue Abschlagsrechnung erstellt werden, da es bereits Entwurf-Abschlagsrechnungen für das zugehörige Angebot gibt.',
    draftPartialInvoiceExistsError:
      'Es kann keine neue Teilrechnung erstellt werden. Vorhandene Teilrechnungsentwürfe müssen zuerst ausgestellt werden.',
  },
  noPdfMessage: 'Keine PDF verfügbar, bitte versuche es später erneut.',
  issueAndSend: 'Senden und abschließen',
  issueAndSendInvoiceDescription: 'Rechnung erstellen und per E-Mail an Kunden senden.',
  issueAndSendQuoteDescription: 'Angebot erstellen und per E-Mail an Kunden senden.',
  issueOnly: 'Nur abschließen',
  issueOnlyInvoiceDescription: 'Rechnung später senden oder als PDF herunterladen.',
  issueOnlyQuoteDescription: 'Angebot später senden oder als PDF herunterladen.',
  loading: 'Vorschau wird geladen...',
  preview: 'Vorschau',
  details: 'Details',
  error: 'Die Vorschau konnte nicht geladen werden',
  deleteDialog: {
    title: 'Entwurf löschen',
    description: 'Du bist dabei, einen Entwurfs zu löschen.',
    doNotShowAgain: 'Nicht mehr anzeigen',
    cancel: 'Abbrechen',
    continue: 'Löschen',
  },
  voidDialog: {
    title: 'Rechnung vollständig stornieren',
    description:
      'Die Rechnung wird storniert und eine Gutschrift über den gesamten Rechnungsbetrags wird automatisch erstellt und verknüpft.',
    cancel: 'Abbrechen',
    continue: 'Stornieren',
  },
  issueDialog: {
    title: 'Unveränderlich nach Ausstellung',
    description:
      'Gemäß Buchhaltungsrichtlinien können ausgestellte Rechnungen nicht geändert werden. Bei Bedarf kannst du eine Gutschrift erstellen, um eine Rechnung zu stornieren.',
    doNotShowAgain: 'Nicht mehr anzeigen',
    cancel: 'Abbrechen',
    continue: 'Weiter',
  },
  markAsPaidDialog: {
    title: 'Rechnung als bezahlt verbuchen',
    description:
      'Bitte überprüfe den Eingang der vollständigen Zahlung. Diese Aktion ist endgültig und kann nicht rückgängig gemacht werden.',
    doNotShowAgain: 'Nicht mehr anzeigen',
    cancel: 'Abbrechen',
    continue: 'Zahlungseingang buchen',
  },
  finalInvoiceConfirmDialog: {
    title: 'Schlussrechnung erstellen',
    description:
      'Sie haben {{count}} Entwurf-Teilrechnung(en) für dieses Angebot. Diese müssen ausgestellt werden, bevor eine Schlussrechnung erstellt werden kann.',
    warning: 'Alle Entwurf-Teilrechnungen werden automatisch ausgestellt, wenn Sie fortfahren.',
    cancel: 'Abbrechen',
    continue: 'Weiter',
    convertAndProceed: 'Entwürfe ausstellen & Schlussrechnung erstellen',
    checking: 'Prüfung auf Entwurf-Teilrechnungen...',
    doNotShowAgain: 'Nicht mehr anzeigen',
  },

  issueAndSendDialog: {
    close: 'Schließen',
    composeEmail: 'E-Mail verfassen',
    title: 'Unveränderlich nach Ausstellung',
    description:
      'Gemäß Buchhaltungsrichtlinien können ausgestellte Rechnungen nicht geändert werden. Bei Bedarf kannst du eine Gutschrift erstellen, um eine Rechnung zu stornieren.',
    doNotShowAgain: 'Nicht mehr anzeigen',
    cancel: 'Abbrechen',
    continue: 'Weiter',
    emailRequired: 'E-Mail ist erforderlich',
    emailInvalid: 'Bitte gib eine gültige E-Mail Adresse ein',
    subjectRequired: 'Betreff ist erforderlich',
    messageRequired: 'Nachricht ist erforderlich',
    to: 'An',
    subject: 'Betreff',
    message: 'Nachricht',
    emailPlaceholder: 'E-Mail-Adresse des Empfängers eingeben',
    subjectPlaceholder: 'Betreffzeile für deine E-Mail hinzufügen',
    messagePlaceholder: 'Schreib hier deine Nachricht',
    invoicePdfAttached: 'Die PDF Rechnung wird angehängt.',
    quotePdfAttached: 'Das PDF Angebot wird angehängt.',
    creditNotePdfAttached: 'Die PDF Gutschrift wird angehängt.',
    invoicePdf: 'Die PDF Rechnung',
    quotePdf: 'Das PDF Angebot',
    creditNotePdf: 'Die PDF Gutschrift',
    jobFiles: 'Auftragsdateien',
    jobFile: 'Auftragsdatei',
    jobFilesPlural: 'Auftragsdateien',
    customFile: 'benutzerdefinierte Datei',
    customFilesPlural: 'benutzerdefinierte Dateien',
    maxFileSize: 'Maximal 10MB',
    pdfAndJobFilesAttached:
      '{{pdfType}} sowie {{fileCount}} {{fileType}} ({{fileSize}}) werden angehängt.',
    pdfAndJobAndCustomFilesAttached:
      '{{pdfType}} sowie {{jobFileCount}} {{jobFileType}} und {{customFileCount}} {{customFileType}} ({{fileSize}}) werden angehängt.',
    fileSizeLimitExceeded: 'Dateigrößen-Limit überschritten (10 MB max)',
    fileSizeLimitWarning: 'Gesamte Dateigröße: {{totalSize}} / 10 MB',
    fileSizeLimitClose: 'Gesamte Dateigröße: {{totalSize}} / 10 MB ({{exactBytes}} Bytes)',
    attachmentsTooLarge:
      'Die Anhänge sind zu groß zum Versenden ({{totalSize}}, maximal {{limit}}). Entferne einige Dateien und versuche es erneut.',
    imageEmbeddingFailed:
      'Diese Bilder konnten nicht ins PDF eingebettet werden und wurden nicht angehängt: {{files}}. Entferne sie oder deaktiviere „Alle ausgewählten Bilder in PDF einbetten“ und versuche es erneut.',
    loadingJobFiles: 'Auftragsdateien werden geladen...',
    errorLoadingJobFiles: 'Fehler beim Laden der Auftragsdateien',
    selectJobFile: 'Wähle eine Auftragsdatei zum Anhängen aus',
    noJobFiles: 'Keine Auftragsdateien gefunden',
    searchFiles: 'Dateien suchen...',
    noFilesFound: 'Keine Dateien für "{{query}}" gefunden',
    uploadCustomFiles: 'Dateien hochladen',
    dragDropFiles: 'oder Dateien hier ablegen',
    customFilesLabel: 'Benutzerdefinierte Anhänge',
    removeFile: 'Datei entfernen',
    sendAndIssue: 'Senden und abschließen',
    sendOnly: 'Senden',
    attachXml: 'XML-Datei anhängen',
    attachXmlTooltip: 'Die XML-Datei wird an die E-Mail angehängt.',
    embedImages: 'Alle ausgewählten Bilder in PDF einbetten',
    embedImagesTooltip:
      'Alle ausgewählten Bilder(png, jpg, jpeg) werden im PDF eingebettet, die anderen Dateien werden wie gewohnt angehängt.',
    companyContactInfoIsMandatory:
      'Damit die XML-Datei übermittelt werden kann, sind die Kontaktinformationen der Firma, also Telefonnummer und E-Mail-Adresse, erforderlich.',
    lineItemsWithDiscountsCannotBeAttached:
      'Die Rechnung enthält Positionen mit Rabatten. Wir unterstützen keine XML-Dateien mit Positionen, die Rabatte haben.',
  },

  cancelRecurringDialog: {
    title: 'Wiederkehrende Rechnung beenden',
    description:
      'Die wiederkehrende Rechnung wird dauerhaft beendet und kann nicht wieder fortgesetzt werden. Es werden keine weiteren Rechnungen erstellt.',
    doNotShowAgain: 'Nicht mehr anzeigen',
    cancel: 'Abbrechen',
    continue: 'Wiederkehrende Rechnung beenden',
  },

  markAsPaidSuccessMessage: 'Rechnung als bezahlt verbucht',
  cancelRecurringSuccessMessage: 'Wiederkehrende Rechnung erfolgreich beendet',
  markAsPartialPaidSuccessMessage: 'Teilzahlung erfolgreich erfasst',
  updatePaymentSuccessMessage: 'Zahlung aktualisiert',
  deletePaymentSuccessMessage: 'Zahlung gelöscht',
  deletePaymentDialog: {
    title: 'Zahlung löschen?',
    description:
      'Die Zahlung über {{amount}} vom {{date}} wird entfernt und der offene Betrag neu berechnet. Die Historie behält einen Eintrag dazu.',
    cancel: 'Abbrechen',
    confirm: 'Zahlung löschen',
  },
  remaining: 'verbleibend',
  markAsDeliveredSuccessMessage: 'Lieferschein als geliefert verbucht',
  issueInvoiceSuccessMessage: 'Rechnung abgeschlossen',
  issueQuoteSuccessMessage: 'Angebot abgeschlossen',
  sendInvoiceSuccessMessage: 'Rechnung gesendet',
  sendQuoteSuccessMessage: 'Angebot gesendet',
  sendQuoteConfirmationSuccessMessage: 'Angebotsbestätigung gesendet',
  sendOrderConfirmationSuccessMessage: 'Auftragsbestätigung gesendet',
  creditNoteSentSuccessMessage: 'Gutschrift gesendet',
  createStornoInvoiceSuccessMessage: 'Stornorechnung erstellt',
  deleteSuccessMessage: 'Entwurf gelöscht',
  deleteDeliveryNoteSuccessMessage: 'Lieferschein gelöscht',
  deleteQuoteConfirmationSuccessMessage: 'Angebotsbestätigung gelöscht',
  deleteOrderConfirmationSuccessMessage: 'Auftragsbestätigung gelöscht',
  issueOrderConfirmationSuccessMessage: 'Auftragsbestätigung erstellt und finalisiert',
  voidInvoiceSuccessMessage: 'Rechnung vollständig storniert',
  voidDeliveryNoteSuccessMessage: 'Lieferschein vollständig storniert',
  acceptSuccessMessage: 'Angebot akzeptiert',
  declineSuccessMessage: 'Angebot abgelehnt',
  acceptQuoteConfirmationSuccessMessage: 'Angebotsbestätigung akzeptiert',
  acceptExpiredQuote: {
    title: 'Abgelaufenes Angebot akzeptieren?',
    description: 'Dieses Angebot ist am {{date}} abgelaufen. Möchten Sie es trotzdem akzeptieren?',
    confirm: 'Trotzdem akzeptieren',
  },
  createInvoiceSuccessMessage: 'Rechnung erstellt',
  createQuoteConfirmationSuccessMessage: 'Angebotsbestätigung erstellt',
  createDeliveryNoteSuccessMessage: 'Lieferschein erstellt',
  createOrderConfirmationSuccessMessage: 'Auftragsbestätigung erstellt',
  finalInvoiceCreatedSuccessMessage: 'Schlussrechnung erstellt',
  history: {
    rule: 'Regel:',
    by: 'von',
    from: 'Von',
    to: 'zu',
    recipients: 'An:',
    documentDraftDeleted: 'Dokumententwurf gelöscht',
    events: {
      status_changed: 'Status aktualisiert',
      receivable_updated: 'Dokument aktualisiert',
      receivable_created: 'Dokument erstellt',
      based_on_receivable_created: 'Folgedokument erstellt',
      mail_sent: 'Als E-Mail gesendet',
      payment_reminder_mail_sent: 'Zahlungserinnerung gesendet',
      overdue_reminder_mail_sent: 'Mahnung gesendet',
      payment_received: 'Zahlung erhalten',
      payment_updated: 'Zahlung korrigiert',
      payment_deleted: 'Zahlung gelöscht',
    },
  },
  tabs: {
    details: 'Details',
    history: 'Verlauf',
  },
  partialInvoice: {
    title: 'Teilrechnung',
    description: 'Wählen Sie Positionen und Mengen für die Teilrechnung aus.',
    createPartialInvoice: 'Teilrechnung erstellen',
    noAvailableItems: 'Keine Positionen für Teilrechnung verfügbar.',
    noItemsSelected: 'Bitte wählen Sie mindestens eine Position aus.',
    success: 'Teilrechnung erfolgreich erstellt.',
    error: 'Fehler beim Erstellen der Teilrechnung.',
    draftQuoteWarning:
      'Dieses Angebot befindet sich derzeit im Entwurfsstatus. Wenn Sie eine Teil- oder Abschlussrechnung erstellen, wird das Angebot automatisch angenommen und kann danach nicht mehr bearbeitet werden.',
    draftPartialsExistTitle: 'Entwurfs-Teilrechnungen vorhanden',
    draftPartialsExistDescription:
      'Bevor Sie eine weitere Teil-/Abschlagsrechnung oder eine Schlussrechnung erstellen können, müssen alle vorhandenen Entwurfs-Teilrechnungen abgeschlossen werden.',
    fillAll: 'Alle ausfüllen',
    clear: 'Leeren',
    lineItem: 'Position',
    available: 'Verfügbar',
    quantity: 'Menge',
    unitPrice: 'Einzelpreis',
    total: 'Gesamt',
    actions: 'Aktionen',
    cancel: 'Abbrechen',
  },
  installmentInvoice: {
    title: 'Abschlagsrechnung',
    description: 'Geben Sie einen Betrag für die Abschlagsrechnung ein.',
    createInstallmentInvoice: 'Abschlagsrechnung erstellen',
    success: 'Abschlagsrechnung erfolgreich erstellt.',
    error: 'Fehler beim Erstellen der Abschlagsrechnung.',
    quoteTotal: 'Angebotssumme',
    alreadyInvoiced: 'Bereits abgerechnet',
    remainingAmount: 'Restbetrag',
    availableAmountNet: 'Verfügbarer Betrag (Netto)',
    amountNet: 'Betrag (Netto)',
    grossAmountDisplay: 'Brutto: {{amount}} (inkl. {{vatRate}}% MwSt.)',
    newRemainingAmount: 'Neuer Restbetrag',
    noRemainingAmount: 'Kein Restbetrag für Abschlagsrechnung verfügbar.',
    willCreateFinalInvoice:
      'Der gesamte verbleibende Betrag wird verwendet. Eine Schlussrechnung wird erstellt.',
    enterAmount: 'Betrag eingeben',
    amountType: 'Betragsart',
    netAmount: 'Nettobetrag',
    grossAmount: 'Bruttobetrag',
    vat19: 'MwSt. 19%',
    preview: 'Vorschau',
    amountRequired: 'Bitte geben Sie einen Betrag größer als 0 ein.',
    amountExceedsRemaining: 'Der eingegebene Betrag überschreitet den Restbetrag.',
    draftQuoteWarning:
      'Dieses Angebot befindet sich derzeit im Entwurfsstatus. Wenn Sie eine Abschlags- oder Schlussrechnung erstellen, wird das Angebot automatisch angenommen und kann danach nicht mehr bearbeitet werden.',
    lineItemDescription: 'Beschreibung',
    lineItemDescriptionOptional: '(optional)',
    lineItemDescriptionPlaceholder: 'Zusätzliche Informationen für diese Abschlagsrechnung...',
  },
  finalInvoice: {
    title: 'Schlussrechnung erstellen',
    description: 'Erstellen Sie eine Schlussrechnung mit allen verbleibenden Positionen.',
    createFinalInvoice: 'Schlussrechnung erstellen',
    noRemainingItems: 'Keine verbleibenden Positionen für Schlussrechnung.',
  },
  draftPartials: {
    title: 'Entwurf-Teilrechnungen gefunden',
    description:
      'Sie haben {{count}} Entwurf-Teilrechnung(en) für dieses Angebot. Diese müssen ausgestellt werden, bevor eine Schlussrechnung erstellt werden kann.',
    warning: 'Alle Entwurf-Teilrechnungen werden automatisch ausgestellt, wenn Sie fortfahren.',
    convertAndProceed: 'Entwürfe ausstellen & Schlussrechnung erstellen',
    cancel: 'Abbrechen',
    checking: 'Prüfung auf Entwurf-Teilrechnungen...',
  },
  issueQuoteForPartial: {
    title: 'Angebot zuerst ausstellen',
    description:
      'Um eine Teilrechnung zu erstellen, muss das Angebot zuerst ausgestellt werden. Dies macht das Angebot offiziell und ermöglicht die Erstellung von Teilrechnungen.',
    warning: 'Nach der Ausstellung kann das Angebot nicht mehr bearbeitet werden.',
    issueQuote: 'Angebot ausstellen',
    cancel: 'Abbrechen',
  },
  cannotCancelInvoiceTitle: 'Rechnung kann nicht storniert werden',
  cannotCancelInvoice: {
    draftInvoicesExist:
      'Diese Teil- oder Abschlagsrechnung kann nicht storniert werden, da es Entwurf-Rechnungen für das zugehörige Angebot gibt. Bitte stellen Sie zuerst alle Entwürfe aus oder löschen Sie diese.',
    finalInvoiceExists:
      'Diese Teil- oder Abschlagsrechnung kann nicht storniert werden, da bereits eine Schlussrechnung für das zugehörige Angebot erstellt wurde.',
    laterPartialsExist:
      'Diese Teil- oder Abschlagsrechnung kann nicht storniert werden, da es spätere Teil- oder Abschlagsrechnungen für das zugehörige Angebot gibt. Nur die zuletzt ausgestellte Teil-/Abschlagsrechnung kann storniert werden.',
  },
} as const

export default invoiceDetails
